import {
  COOKIE_BANNER_APPEAR_TIMEOUT_MS,
  COOKIE_BANNER_CLOSED_COOKIE,
  COOKIE_BANNER_ID,
  COOKIE_BANNER_POLL_MS,
} from '@/app/lib/geolocation'
import { getCookie } from '@/app/lib/utils'

function isBannerVisible(): boolean {
  const banner = document.getElementById(COOKIE_BANNER_ID)
  if (!banner || banner.getClientRects().length === 0) return false
  return getComputedStyle(banner).visibility !== 'hidden'
}

/**
 * Resuelve cuando el banner de cookies de OneTrust (inyectado por GTM, fuera
 * de este repo) ya no está pendiente. Antes de pedir la ubicación hay que
 * esperarlo: el aviso de permiso del navegador se queda con el foco, y el
 * primer clic en el banner solo lo apartaba sin llegar al botón.
 *
 * - Cookie `OptanonAlertBoxClosed` presente ⇒ ya se decidió antes: al momento.
 * - Banner visible ⇒ al cerrarse (aceptar/rechazar/configurar).
 * - Banner que no aparece en `COOKIE_BANNER_APPEAR_TIMEOUT_MS` (bloqueado por
 *   un adblocker, GTM caído) ⇒ se sigue igualmente.
 *
 * Sondeo y no `MutationObserver`: OneTrust lo oculta con estilos y clases
 * que cambia a su manera, y unas pocas lecturas cada 250 ms no cuestan nada.
 */
export function waitForCookieBannerClosed(signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (getCookie(COOKIE_BANNER_CLOSED_COOKIE) !== undefined) {
      resolve()
      return
    }

    const startedAt = Date.now()
    let seen = false
    const intervalId = window.setInterval(check, COOKIE_BANNER_POLL_MS)
    signal.addEventListener('abort', () => window.clearInterval(intervalId), { once: true })
    check()

    function check(): void {
      if (signal.aborted) return
      const visible = isBannerVisible()
      if (visible) seen = true
      const decided = getCookie(COOKIE_BANNER_CLOSED_COOKIE) !== undefined
      const closed = seen && !visible
      const neverShown = !seen && Date.now() - startedAt >= COOKIE_BANNER_APPEAR_TIMEOUT_MS
      if (decided || closed || neverShown) {
        window.clearInterval(intervalId)
        resolve()
      }
    }
  })
}
